import { Box, Grid, Typography } from '@mui/material';
import CurrencyFormatter from '@apigrowfinance/pbolt-currency-formatter';
import TextDisplay from '@apigrowfinance/pbolt-text-display';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { FormattedMessage } from 'react-intl';

import { TransactionAmount, TransactionTax } from '@clients/transactionsClient';
import { useAccount } from '@contexts/AccountProvider';

export interface TransactionBreakdownDetailsProps {
    tax: TransactionTax[];
    amount: TransactionAmount[];
    isLoading: boolean;
}

const TransactionBreakdownDetails = ({
    tax,
    amount,
    isLoading,
}: TransactionBreakdownDetailsProps) => {
    const { theme } = useTheme();
    const { program, isLoading: isLoadingAccount } = useAccount();
    const currencyCode = program?.currency_numeric_code;
    isLoading = isLoading || isLoadingAccount;
    return (
        <Grid
            container
            item
            sx={{
                bgcolor: theme?.palette?.background?.paper,
                pt: 4.375,
                pl: 2.625,
                py: 4,
                px: 3,
                mb: 0.25,
            }}
            flexDirection="column">
            {tax?.length > 0 && (
                <>
                    <Box>
                        <Typography variant="h6" component="h3">
                            <FormattedMessage
                                id="TransactionDetails.taxes"
                                defaultMessage="Taxes"
                            />
                        </Typography>
                    </Box>
                    <Grid
                        container
                        item
                        xs={12}
                        pt={4}
                        pb={amount?.length > 1 ? 4 : 0}>
                        {tax.map((textItem) => (
                            <Grid item xs={12} sm={3} key={textItem.type}>
                                <TextDisplay
                                    label={textItem.type}
                                    value={
                                        <CurrencyFormatter
                                            amount={textItem.value}
                                            currency={currencyCode}
                                        />
                                    }
                                    isLoading={isLoading}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}
            {/* We only show breakdown if there is more than 1 amount. If there is only one no breakdown required */}
            {amount?.length > 1 && (
                <>
                    <Typography variant="h6" component="h3">
                        <FormattedMessage
                            id="AccountTransactionDetails.transactionBreakdown"
                            defaultMessage="Transaction Breakdown"
                        />
                    </Typography>
                    <Grid container item xs={12} pt={4}>
                        {amount.map((amountItem) => (
                            <Grid item xs={12} sm={3} key={amountItem?.type}>
                                <TextDisplay
                                    label={amountItem?.type}
                                    value={
                                        <CurrencyFormatter
                                            testId={`amount-type-${amountItem?.type}`}
                                            amount={amountItem?.value}
                                            currency={amountItem?.currency}
                                        />
                                    }
                                    isLoading={isLoading}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}
        </Grid>
    );
};

export default TransactionBreakdownDetails;
